import React from "react";
import {
  Card,
  Stack,
  Box,
  Typography,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { AnswerCard } from "./StyledComponents";
import SongCluesSection from "./SongCluesSection";

const QuestionCard = ({
  question,
  selectedBatchConfig,
  isSelected,
  onToggleSelection,
  onApprove,
  onReject,
  filteredSongs,
  songsLoading,
  questionClues,
  selectedSongsForClues,
  generatingClues,
  onBulkSelectSongsForClues,
  onSongsForCluesChange,
  onGenerateClues,
  onRefreshClues,
  onDeleteClue,
}) => {
  return (
    <Card
      sx={{
        backgroundColor: "#1A1A1A",
        border: "1px solid #333",
        borderRadius: "12px",
        mb: 2,
        p: 3,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelection}
          disabled={question.status === "published"}
          sx={{ color: "#4CAF50", mt: 0.5 }}
        />
        <Box sx={{ flex: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ color: "#fff", mb: 1.5, fontWeight: 600 }}
              >
                {question.question_text}
              </Typography>

              {/* Metadata Section */}
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#4CAF50", mb: 1.5, fontWeight: 600 }}
                >
                  Question Metadata
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", display: "block", mb: 0.5 }}
                    >
                      Answer Type
                    </Typography>
                    <Chip
                      label={question.answer_type_name}
                      size="small"
                      sx={{
                        backgroundColor: "#2196F3",
                        color: "#fff",
                        fontWeight: 500,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", display: "block", mb: 0.5 }}
                    >
                      Difficulty Level
                    </Typography>
                    {question.level_name ? (
                      <Chip
                        label={question.level_name}
                        size="small"
                        sx={{
                          backgroundColor: "#FF9800",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ) : selectedBatchConfig?.difficulties?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedBatchConfig.difficulties.map((d) => (
                          <Chip
                            key={d.id}
                            label={d.name}
                            size="small"
                            sx={{
                              backgroundColor: "#FF9800",
                              color: "#fff",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#888" }}>
                        —
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", display: "block", mb: 0.5 }}
                    >
                      Mood
                    </Typography>
                    {question.mood_name ? (
                      <Chip
                        label={question.mood_name}
                        size="small"
                        sx={{
                          backgroundColor: "#9C27B0",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ) : selectedBatchConfig?.moods?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedBatchConfig.moods.map((m) => (
                          <Chip
                            key={m.id}
                            label={m.name}
                            size="small"
                            sx={{
                              backgroundColor: "#9C27B0",
                              color: "#fff",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#888" }}>
                        —
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", display: "block", mb: 0.5 }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={question.status.replace(/_/g, " ")}
                      size="small"
                      color={
                        question.status === "approved_for_live"
                          ? "success"
                          : question.status === "rejected"
                            ? "error"
                            : question.status === "published"
                              ? "primary"
                              : "default"
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", display: "block", mb: 0.5 }}
                    >
                      Topic
                    </Typography>
                    {question.topic_name ? (
                      <Chip
                        label={question.topic_name}
                        size="small"
                        sx={{
                          backgroundColor: "#00BCD4",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ) : selectedBatchConfig?.topics?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedBatchConfig.topics.map((t) => (
                          <Chip
                            key={t.id}
                            label={t.name}
                            size="small"
                            sx={{
                              backgroundColor: "#00BCD4",
                              color: "#fff",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#888" }}>
                        —
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", display: "block", mb: 0.5 }}
                    >
                      Subtopic
                    </Typography>
                    {question.subtopic_name ? (
                      <Chip
                        label={question.subtopic_name}
                        size="small"
                        sx={{
                          backgroundColor: "#009688",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ) : selectedBatchConfig?.subtopics?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedBatchConfig.subtopics.map((st) => (
                          <Chip
                            key={st.id}
                            label={st.name}
                            size="small"
                            sx={{
                              backgroundColor: "#009688",
                              color: "#fff",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#888" }}>
                        —
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Approve">
                <span>
                  <IconButton
                    size="small"
                    onClick={onApprove}
                    disabled={question.status === "approved_for_live"}
                    sx={{ color: "#4CAF50" }}
                  >
                    <ApproveIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject">
                <span>
                  <IconButton
                    size="small"
                    onClick={onReject}
                    disabled={question.status === "rejected"}
                    sx={{ color: "#f44336" }}
                  >
                    <RejectIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Answers Section */}
          <Box mb={3}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#4CAF50", mb: 1.5, fontWeight: 600 }}
            >
              Answer Options
            </Typography>
            {question.answers && question.answers.length > 0 ? (
              question.answers.map((answer, idx) => {
                const isCorrect =
                  !!answer.is_correct || answer.is_correct === 1;
                return (
                  <AnswerCard key={idx} isCorrect={isCorrect}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          minWidth: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: isCorrect ? "#4CAF50" : "#f44336",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      >
                        {isCorrect ? (
                          <CheckIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <RejectIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            color: isCorrect ? "#4CAF50" : "#fff",
                            fontWeight: isCorrect ? 600 : 400,
                            mb: 0.5,
                          }}
                        >
                          {String.fromCharCode(65 + idx)}. {answer.answer_text}
                        </Typography>
                        {answer.answer_reason && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              borderRadius: "6px",
                              backgroundColor: isCorrect
                                ? "rgba(76, 175, 80, 0.1)"
                                : "rgba(244, 67, 54, 0.1)",
                              border: `1px solid ${isCorrect ? "#4CAF50" : "#f44336"}`,
                              borderLeft: `4px solid ${isCorrect ? "#4CAF50" : "#f44336"}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isCorrect ? "#4CAF50" : "#f44336",
                                fontWeight: 600,
                                display: "block",
                                mb: 0.5,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              {isCorrect
                                ? "✓ Why This Is Correct"
                                : "✗ Why This Is Wrong"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isCorrect ? "#81C784" : "#EF5350",
                                fontStyle: "italic",
                                lineHeight: 1.6,
                              }}
                            >
                              {answer.answer_reason}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </AnswerCard>
                );
              })
            ) : (
              <Typography variant="body2" sx={{ color: "#888" }}>
                No answers available
              </Typography>
            )}
          </Box>

          {/* Song Clues Section */}
          <SongCluesSection
            question={question}
            filteredSongs={filteredSongs}
            songsLoading={songsLoading}
            questionClues={questionClues}
            selectedSongsForClues={selectedSongsForClues}
            generatingClues={generatingClues}
            onBulkSelectSongs={onBulkSelectSongsForClues}
            onSongsChange={onSongsForCluesChange}
            onGenerateClues={onGenerateClues}
            onRefreshClues={onRefreshClues}
            onDeleteClue={onDeleteClue}
          />
        </Box>
      </Stack>
    </Card>
  );
};

export default QuestionCard;
